import { Form, Formik } from 'formik';
import React, { Component, ReactNode } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { locale } from '../../common/localization/localizationService';
import { debounceEventHandler } from '../shared/submitFormUtility';
import { DatePicker } from '../shared/formikFields';
import yup from '../../common/validation';
import moment from 'moment';
import { IFraction, SelectOption } from '../../models/Fraction.model';
import FractionsMultiselect from './FractionsMulitselect';
import OrdersService from './ordersService';
import { Toastr } from '../../utils/Toastr';
import { Spinner2 } from '../shared/spinner2';
import OrdersStatusSelector from './OrderStatusSelector';
import { IOrderSatus, orderStatuses } from './OrderStatus';

export interface UpdatedOrder {
  customerId: number;
  orderId: number;
  date: string;
  ordinaryDate: string;
  expires: string;
  fractions: Array<IFraction>;
  status?: IOrderSatus;
}

export interface IUpdateOrderDialogProps {
  order: any;
  fractions: Array<IFraction>;
  show: boolean;
  onHide: (renderer: boolean) => void;
}

export interface IUpdateOrderDialogState {
  selectedFractions: Array<IFraction>;
  isLoading: boolean;
  updatedStatus: IOrderSatus;
}

export default class UpdateOrderDialog extends Component<
  IUpdateOrderDialogProps,
  IUpdateOrderDialogState
> {
  public readonly state: IUpdateOrderDialogState = {
    selectedFractions: null,
    isLoading: false,
    updatedStatus: null,
  };

  private async onSubmit(order: any): Promise<void> {
    this.setState({ ...this.state, isLoading: true });
    const updatedOrder: UpdatedOrder = {
      date: this.formatDate(order.date),
      ordinaryDate: this.formatDate(order.ordinaryDate),
      expires: this.formatDate(order.expires),
      fractions: this.state.selectedFractions,
      customerId: order.customerId,
      orderId: order.orderId,
    };
    if (this.state.updatedStatus) {
      updatedOrder.status = this.state.updatedStatus;
    }
    try {
      await OrdersService.updateOrder(updatedOrder);
      Toastr.success(locale.orders._updateSuccess);
    } catch (error) {
      Toastr.error(error);
    } finally {
      this.setState({ ...this.state, isLoading: false });
      this.props.onHide(true);
    }
  }

  private getOrdinaryDateValidationMessage(date: Date): string {
    return locale.orders._ordinaryDateValidation.replace(
      '{date}',
      moment(date).format('DD.MM.YYYY')
    );
  }

  private getSchema(isStarted: boolean): void {
    if (isStarted) {
      return yup.object().shape({
        date: yup.date().required().label(locale.orders._date),
        ordinaryDate: yup.date().required().label(locale.orders._ordinaryDate),
        expires: yup.date().required().label(locale.orders._expires),
      });
    } else {
      const today = moment().startOf('day').toDate();
      return yup.object().shape({
        date: yup.date().required().min(today).label(locale.orders._date),
        ordinaryDate: yup
          .date()
          .required()
          .min(today, this.getOrdinaryDateValidationMessage(today))
          .label(locale.orders._ordinaryDate),
        expires: yup
          .date()
          .required()
          .min(today)
          .when('ordinaryDate', (ordinaryDate: Date, schema: any) => {
            return ordinaryDate
              ? schema.min(ordinaryDate, locale.orders._earlierDateValidation)
              : schema;
          })
          .label(locale.orders._expires),
      });
    }
  }

  private fractionsChange(fractions: Array<SelectOption>): void {
    const selectedFractions: Array<IFraction> = fractions?.length
      ? fractions.map((f) => {
          const fraction: IFraction = {
            fractionId: f.value,
            name: f.label,
          };
          return fraction;
        })
      : [];
    this.setState({ ...this.state, selectedFractions: selectedFractions });
  }

  private formatDate(date: Date): string {
    const offset = date.getTimezoneOffset() * 60000;
    const localISOTime = new Date(date.getTime() - offset)
      .toISOString()
      .slice(0, 19);

    return localISOTime;
  }

  private checkOrderStarted(order: any): boolean {
    let statusId = -1;
    if (order?.orderStatus) {
      statusId = order.orderStatus.orderStatusId;
    }
    return statusId !== 0;
  }

  private handleOrderStatusChange(
    selectedOrderStatus: Record<string, IOrderSatus>
  ) {
    this.setState({ ...this.state, updatedStatus: selectedOrderStatus.value });
  }

  private getOrderDisplayName(order: any): string {
    if (order.message) {
      return order.message;
    } else {
      return order.routeData.name;
    }
  }

  public render(): ReactNode {
    const { order, show, onHide, fractions } = this.props;
    order.ordinaryDate = new Date(order.ordinaryDate);
    order.expires = new Date(order.expires);
    const isStarted = this.checkOrderStarted(order);
    const modalTitle = this.getOrderDisplayName(order);
    return (
      <Formik
        initialValues={order}
        onSubmit={(updatedOrder: any) => this.onSubmit(updatedOrder)}
        validationSchema={this.getSchema(isStarted)}
      >
        {({ handleSubmit }) => (
          <Modal show={show} onHide={() => onHide(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Spinner2 show={this.state.isLoading} />
              <Form
                noValidate
                onSubmit={handleSubmit}
                className="update-order-dialog-form"
              >
                {isStarted && (
                  <h3 className="not-editable-warning">
                    {locale.orders._notEditableWarning}
                  </h3>
                )}
                <DatePicker
                  label={locale.orders._date}
                  name="date"
                  popupClass="date-picker-popup-above-modal"
                  disabled={isStarted}
                />
                <DatePicker
                  label={locale.orders._ordinaryDate}
                  name="ordinaryDate"
                  popupClass="date-picker-popup-above-modal"
                  disabled={isStarted}
                />
                <DatePicker
                  label={locale.orders._expires}
                  name="expires"
                  popupClass="date-picker-popup-above-modal"
                />
                <FractionsMultiselect
                  fractions={fractions}
                  selectedFractions={order.fractions as Array<IFraction>}
                  onFractionOptionChange={this.fractionsChange.bind(this)}
                  disabled={isStarted}
                  key={'fractionId'}
                />
                {isStarted && (
                  <div className="order-status-selector-row">
                    <label>{locale.orders._status}</label>
                    <OrdersStatusSelector
                      statuses={orderStatuses}
                      selectedStatus={
                        orderStatuses.find(
                          (x) =>
                            x.orderStatusId == order.orderStatus.orderStatusId
                        )?.status ?? ''
                      }
                      handleChange={this.handleOrderStatusChange.bind(this)}
                    />
                  </div>
                )}
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => onHide(false)}>
                {locale.vehicles._cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                onClick={debounceEventHandler(handleSubmit, 250)}
              >
                {locale.vehicles._update}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    );
  }
}
